// MP4 recorder: wraps WebCodecs (H.264 video + AAC audio) and mp4-muxer into a
// small push API for the player's capture path. Config is the spike-validated set
// (avc1.640028 / mp4a.40.2 / fastStart in-memory). Degrades gracefully: if WebCodecs
// is unavailable the recorder no-ops and finish() returns null — playback unaffected.
import { Muxer, ArrayBufferTarget } from "mp4-muxer";

// MediaStreamTrackProcessor is not in lib.dom yet (Insertable Streams). Minimal type.
interface TrackProcessorCtor {
  new (init: { track: MediaStreamTrack }): { readable: ReadableStream<AudioData> };
}

export interface RecorderDeps {
  VideoEncoder: typeof VideoEncoder;
  AudioEncoder?: typeof AudioEncoder;
  VideoFrame: typeof VideoFrame;
  Muxer: typeof Muxer;
  ArrayBufferTarget: typeof ArrayBufferTarget;
  MediaStreamTrackProcessor?: TrackProcessorCtor;
}

export interface Mp4RecorderOptions {
  dims: { w: number; h: number };
  fps?: number;
  videoBitrate?: number;
  audioTrack?: MediaStreamTrack | null;
  /** Real AudioContext sample rate (device-native, often 44100). Defaults to 48000. */
  audioSampleRate?: number;
  /** Real captured channel count. Defaults to 2. */
  audioChannels?: number;
  deps?: Partial<RecorderDeps>;
}

const VIDEO_CODECS = ["avc1.640028", "avc1.4d0028", "avc1.42e01e"]; // High@4.0 → Main → Baseline fallback
const DEFAULT_AUDIO_SAMPLE_RATE = 48000;
const DEFAULT_AUDIO_CHANNELS = 2;

function resolveDeps(over?: Partial<RecorderDeps>): RecorderDeps {
  const g = globalThis as unknown as Record<string, unknown>;
  return {
    VideoEncoder: (over?.VideoEncoder ?? g.VideoEncoder) as typeof VideoEncoder,
    AudioEncoder: (over?.AudioEncoder ?? g.AudioEncoder) as typeof AudioEncoder | undefined,
    VideoFrame: (over?.VideoFrame ?? g.VideoFrame) as typeof VideoFrame,
    Muxer: over?.Muxer ?? Muxer,
    ArrayBufferTarget: over?.ArrayBufferTarget ?? ArrayBufferTarget,
    MediaStreamTrackProcessor: (over?.MediaStreamTrackProcessor ?? g.MediaStreamTrackProcessor) as TrackProcessorCtor | undefined,
  };
}

/** The sink contract shared by Mp4Recorder (Tier-A, WebCodecs) and
 *  MediaRecorderSink (Tier-B, canvas.captureStream + MediaRecorder). */
export interface CaptureRecorder {
  start(): Promise<boolean>;
  pushVideoFrame(source: CanvasImageSource, timestampMicros: number): void;
  finish(): Promise<Blob | null>;
  abort(): void;
}

export class Mp4Recorder implements CaptureRecorder {
  private deps: RecorderDeps;
  private opts: Required<Pick<Mp4RecorderOptions, "dims" | "fps" | "videoBitrate">> & { audioTrack: MediaStreamTrack | null; audioSampleRate: number; audioChannels: number };
  private muxer: Muxer<ArrayBufferTarget> | null = null;
  private videoEncoder: VideoEncoder | null = null;
  private audioEncoder: AudioEncoder | null = null;
  private audioReader: ReadableStreamDefaultReader<AudioData> | null = null;
  private frameCount = 0;
  private keyEveryN: number;
  private frameDurationUs: number;
  private started = false;
  private aborted = false;
  private lastTs = -1;           // enforce strictly-increasing video timestamps
  private lastEncodedTs = -Infinity; // throttle to the configured fps (rAF outruns it)

  constructor(options: Mp4RecorderOptions) {
    this.deps = resolveDeps(options.deps);
    this.opts = {
      dims: options.dims,
      fps: options.fps ?? 30,
      videoBitrate: options.videoBitrate ?? 10_000_000,
      audioTrack: options.audioTrack ?? null,
      audioSampleRate: options.audioSampleRate ?? DEFAULT_AUDIO_SAMPLE_RATE,
      audioChannels: options.audioChannels ?? DEFAULT_AUDIO_CHANNELS,
    };
    this.keyEveryN = this.opts.fps * 2; // ~2s GOP (in ENCODED frames)
    this.frameDurationUs = 1e6 / this.opts.fps;
  }

  static isSupported(deps?: Partial<RecorderDeps>): boolean {
    const d = resolveDeps(deps);
    // Require the FULL pipeline (video + audio), not just video. iOS WebKit exposes
    // VideoEncoder/VideoFrame but not AudioEncoder (<26) nor MediaStreamTrackProcessor
    // on Window — so a video-only gate would show the Export button on iPhone and then
    // produce a SILENT video-only MP4. Gating on all four hides the button cleanly on
    // iOS until a MediaRecorder fallback ships (see the mobile-export audit), and
    // matches the audio runtime gate in start() (wantAudio).
    return (
      typeof d.VideoEncoder === "function" &&
      typeof d.VideoFrame === "function" &&
      typeof d.AudioEncoder === "function" &&
      typeof d.MediaStreamTrackProcessor === "function"
    );
  }

  private async pickVideoCodec(): Promise<string | null> {
    const { VideoEncoder: VE } = this.deps;
    const probe = VE as unknown as { isConfigSupported?: (c: VideoEncoderConfig) => Promise<VideoEncoderSupport> };
    for (const codec of VIDEO_CODECS) {
      const cfg: VideoEncoderConfig = { codec, width: this.opts.dims.w, height: this.opts.dims.h, bitrate: this.opts.videoBitrate, framerate: this.opts.fps };
      try {
        if (!probe.isConfigSupported) return codec; // can't probe → trust the primary
        const res = await probe.isConfigSupported(cfg);
        if (res?.supported) return codec;
      } catch { /* try next */ }
    }
    return null;
  }

  /** Configure encoders + muxer and begin pulling audio. Returns false (no-op) if unsupported. */
  async start(): Promise<boolean> {
    if (!Mp4Recorder.isSupported(this.deps)) return false;

    const codec = await this.pickVideoCodec();
    if (!codec) return false;

    const { dims, fps } = this.opts;
    const sampleRate = this.opts.audioSampleRate;
    const channels = this.opts.audioChannels;
    // Gate audio on MediaStreamTrackProcessor too: without it the pump can't feed the
    // encoder, so declaring an audio track would yield an empty (malformed) audio stream.
    const wantAudio = !!this.opts.audioTrack && typeof this.deps.AudioEncoder === "function" && typeof this.deps.MediaStreamTrackProcessor === "function";

    const muxer = new this.deps.Muxer({
      target: new this.deps.ArrayBufferTarget(),
      video: { codec: "avc", width: dims.w, height: dims.h, frameRate: fps },
      ...(wantAudio ? { audio: { codec: "aac", numberOfChannels: channels, sampleRate } } : {}),
      fastStart: "in-memory",
      firstTimestampBehavior: "offset",
    }) as Muxer<ArrayBufferTarget>;
    this.muxer = muxer;

    this.videoEncoder = new this.deps.VideoEncoder({
      output: (chunk, meta) => { try { muxer.addVideoChunk(chunk, meta); } catch { /* post-finalize */ } },
      error: (e) => { console.warn("[capture] video encoder error", e); },
    });
    this.videoEncoder.configure({ codec, width: dims.w, height: dims.h, bitrate: this.opts.videoBitrate, framerate: fps });

    if (wantAudio) {
      const AE = this.deps.AudioEncoder!;
      this.audioEncoder = new AE({
        output: (chunk, meta) => { try { muxer.addAudioChunk(chunk, meta); } catch { /* post-finalize */ } },
        error: (e) => { console.warn("[capture] audio encoder error", e); },
      });
      this.audioEncoder.configure({ codec: "mp4a.40.2", sampleRate, numberOfChannels: channels, bitrate: 128_000 });
      this.startAudioPump();
    }

    this.started = true;
    return true;
  }

  private startAudioPump(): void {
    const MSTP = this.deps.MediaStreamTrackProcessor;
    const track = this.opts.audioTrack;
    if (!MSTP || !track || !this.audioEncoder) return; // track still declared in muxer; just no live samples in tests
    try {
      const processor = new MSTP({ track });
      const reader = processor.readable.getReader();
      this.audioReader = reader;
      const pump = async () => {
        try {
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            if (value) {
              if (this.audioEncoder && this.audioEncoder.state === "configured" && !this.aborted) {
                this.audioEncoder.encode(value);
              }
              value.close();
            }
          }
        } catch { /* reader cancelled on finish/abort */ }
      };
      void pump();
    } catch (e) {
      console.warn("[capture] audio pump failed", e);
    }
  }

  /** Push one composited frame. `timestampMicros` should come from the audio master clock. */
  pushVideoFrame(source: CanvasImageSource, timestampMicros: number): void {
    if (!this.started || this.aborted || !this.videoEncoder) return;
    // Throttle to the configured fps: rAF fires at the display refresh (~60/120Hz), but
    // the encoder is budgeted for `fps`. Drop frames that arrive sooner than ~one frame
    // period after the last ENCODED one → correct GOP + rate-control + frame rate.
    const rounded = Math.round(timestampMicros);
    if (rounded < this.lastEncodedTs + this.frameDurationUs * 0.9) return;
    try {
      const ts = Math.max(rounded, this.lastTs + 1);
      this.lastTs = ts;
      this.lastEncodedTs = ts;
      const frame = new this.deps.VideoFrame(source, { timestamp: ts, duration: Math.round(this.frameDurationUs) });
      const keyFrame = this.frameCount % this.keyEveryN === 0;
      this.videoEncoder.encode(frame, { keyFrame });
      frame.close();
      this.frameCount++;
    } catch (e) {
      console.warn("[capture] pushVideoFrame failed", e);
    }
  }

  /** Stop the audio reader, flush both encoders, finalize → Blob. Null if not started/aborted. */
  async finish(): Promise<Blob | null> {
    if (!this.started || this.aborted || !this.muxer || !this.videoEncoder) return null;
    try {
      await this.stopAudioReader();
      await this.videoEncoder.flush();
      if (this.audioEncoder) await this.audioEncoder.flush();
      this.muxer.finalize();
      const buffer = (this.muxer.target as ArrayBufferTarget).buffer;
      this.cleanup(); // closes the encoders (releases codec/hardware handles)
      return new Blob([buffer], { type: "video/mp4" });
    } catch (e) {
      console.warn("[capture] finish failed", e);
      this.cleanup();
      return null;
    }
  }

  /** Discard the in-progress capture (pause/seek mid-run). */
  abort(): void {
    this.aborted = true;
    void this.stopAudioReader();
    this.cleanup();
  }

  private async stopAudioReader(): Promise<void> {
    if (this.audioReader) {
      try { await this.audioReader.cancel(); } catch { /* ignore */ }
      this.audioReader = null;
    }
  }

  private cleanup(): void {
    this.started = false;
    try { this.opts.audioTrack?.stop(); } catch { /* ignore */ }
    // Close the encoders to release codec/hardware handles (both finish + abort paths).
    try { if (this.videoEncoder && this.videoEncoder.state !== "closed") this.videoEncoder.close(); } catch { /* ignore */ }
    try { if (this.audioEncoder && this.audioEncoder.state !== "closed") this.audioEncoder.close(); } catch { /* ignore */ }
    this.videoEncoder = null;
    this.audioEncoder = null;
    this.muxer = null;
  }
}
